import React, {forwardRef, Fragment, ReactElement, useState, useRef, useEffect} from 'react';
import {NavLink} from 'react-router-dom';
import clsx from 'clsx';
import {Orientation} from '@ui/forms/orientation';
import {useCustomMenu} from './use-custom-menu';
import {Trans} from '@ui/i18n/trans';
import {IconSize} from '@ui/icons/svg-icon';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {Tooltip} from '@ui/tooltip/tooltip';

type MatchDescendants = undefined | boolean | ((to: string) => boolean);

const MIN_COLS = 1;
const MAX_COLS = 4;
const MAX_ROWS = 5; // max items per column

function getDynamicColumns<T>(items: T[]): T[][] {
  const total = items.length;

  const colCount = Math.min(
    MAX_COLS,
    Math.max(MIN_COLS, Math.ceil(total / MAX_ROWS))
  );

  const rowsPerCol = Math.ceil(total / colCount);
  const cols: T[][] = [];

  for (let i = 0; i < total; i += rowsPerCol) {
    cols.push(items.slice(i, i + rowsPerCol));
  }

  return cols;
}

interface CustomMenuProps {
  className?: string;
  matchDescendants?: MatchDescendants;
  iconClassName?: string;
  iconSize?: IconSize;
  itemClassName?:
    | string
    | ((props: {isActive: boolean; item: MenuItemConfig}) => string | undefined);
  gap?: string;
  menu?: string | MenuConfig;
  children?: (menuItem: MenuItemConfig) => ReactElement;
  orientation?: Orientation;
  onlyShowIcons?: boolean;
  unstyled?: boolean;
}

export function CustomMenu({
  className,
  iconClassName,
  itemClassName,
  gap = 'gap-30',
  menu: menuOrPosition,
  orientation = 'horizontal',
  children,
  matchDescendants,
  onlyShowIcons,
  iconSize,
  unstyled = false,
}: CustomMenuProps) {
  const menu = useCustomMenu(menuOrPosition);
  if (!menu) return null;

  return (
    <div
      className={clsx(
        'flex',
        gap,
        orientation === 'vertical' ? 'flex-col items-start' : 'items-center',
        className,
      )}
      data-menu-id={menu.id}
    >
      {menu.items.map(item => {
        if (children) return children(item);
        return (
          <CustomMenuItem
            unstyled={unstyled}
            onlyShowIcon={onlyShowIcons}
            matchDescendants={matchDescendants}
            iconClassName={iconClassName}
            iconSize={iconSize}
            className={props =>
              typeof itemClassName === 'function'
                ? itemClassName({...props, item})
                : itemClassName
            }
            key={item.id}
            item={item}
          />
        );
      })}
    </div>
  );
}

function useHoverDelay(enter = 0, leave = 140) {
  const [open, setOpen] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  const on = () => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setOpen(true), enter);
  };
  const off = () => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setOpen(false), leave);
  };
  return {open, on, off};
}

function ChevDown({open}: {open: boolean}) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 10 10" fill="none"
      style={{
        flexShrink: 0, marginLeft: 3,
        transition: 'transform 0.18s ease, opacity 0.18s',
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        opacity: open ? 0.75 : 0.4,
      }}
    >
      <path d="M1.5 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}


function ChevRight() {
  return (
    <svg
      width="11" height="11" viewBox="0 0 12 12" fill="none"
      style={{flexShrink: 0, marginLeft: 'auto', paddingLeft: 8, opacity: 0.35}}
    >
      <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function DropRow({
  item, hovered, hasChildren, icon, label,
}: {
  item: MenuItemConfig;
  hovered: boolean;
  hasChildren: boolean;
  icon: React.ReactNode;
  label: React.ReactNode;
}) {
  return (
    <>
      {icon}
      {label}
      {hasChildren && <ChevRight />}
    </>
  );
}


interface DropPanelProps {
  items: MenuItemConfig[];
  open: boolean;
  depth: number;
  anchorEl?: HTMLElement | null;
  navbarEl?: HTMLElement | null;
  unstyled?: boolean;
  onlyShowIcon?: boolean;
  matchDescendants?: MatchDescendants;
  iconClassName?: string;
  iconSize?: IconSize;
  className?: (props: {isActive: boolean}) => string | undefined;
}

function DropPanel({
  items, open, depth, anchorEl, navbarEl,
  unstyled, onlyShowIcon, matchDescendants,
  iconClassName, iconSize, className,
}: DropPanelProps) {
  const [pos, setPos] = useState({top: 0, left: 0});

  // Only depth=0 needs fixed positioning
  useEffect(() => {
    if (depth !== 0 || !open || !anchorEl || !navbarEl) return;
    const aRect = anchorEl.getBoundingClientRect();
    const nRect = navbarEl.getBoundingClientRect();

 

    let left = aRect.left;
    if (left + estimatedWidth > window.innerWidth - 8) {
      left = window.innerWidth - estimatedWidth - 8;
    }
    const GAP = 12; 
    setPos({top: nRect.bottom, left: Math.max(8, left)});
  }, [open, depth, anchorEl, navbarEl, items.length]);

const columns = getDynamicColumns(items);
const estimatedWidth = columns.length * 196;

  const panelStyle: React.CSSProperties = {
    position: depth === 0 ? 'fixed' : 'absolute',
    zIndex: 9999 + depth,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 0,
    padding: '20px 8px',
    marginTop: 4,
    background: '#fff',
    boxShadow: '0 20px 45px rgba(0,0,0,0.12)',
    borderRadius: depth === 0 ? '16px 16px 16px 16px' : 7,
    ...(depth === 0
      ? { top: pos.top, left: pos.left}
      : {top: -8, left: '100%', marginLeft: 4}),
    opacity: open ? 1 : 0,
    transform: open
  ? 'translateY(0) scale(1)'
  : 'translateY(-10px) scale(0.96)',
transition: 'opacity 0.2s ease, transform 0.2s ease',
    pointerEvents: open ? 'auto' : 'none',
  };

  return (
    <div style={panelStyle}>
      {columns.map((col, ci) => (
        <Fragment key={ci}>
          <div style={{display: 'flex', flexDirection: 'column', minWidth: 160, padding: '0 16px'}}>
            {col.map(child => (
              <DropItem
                key={child.id}
                item={child}
                depth={depth}
                unstyled={unstyled}
                onlyShowIcon={onlyShowIcon}
                matchDescendants={matchDescendants}
                iconClassName={iconClassName}
                iconSize={iconSize}
                className={className}
              />
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

interface DropItemProps {
  item: MenuItemConfig;
  depth: number;
  unstyled?: boolean;
  onlyShowIcon?: boolean;
  matchDescendants?: MatchDescendants;
  iconClassName?: string;
  iconSize?: IconSize;
  className?: (props: {isActive: boolean}) => string | undefined;
}

function DropItem({
  item, depth, unstyled, onlyShowIcon, matchDescendants,
  iconClassName, iconSize = 'sm', className,
}: DropItemProps) {
  const hasChildren = !!(item.children?.length);
  const {open, on, off} = useHoverDelay(0, 140);
  const [hovered, setHovered] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const isActive = hovered || (hasChildren && open);

  const IconCmp = item.icon && createSvgIconFromTree(item.icon);
  const icon = IconCmp ? <IconCmp size={iconSize} className={iconClassName} /> : null;
  const label = <Trans message={item.label} />;

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 8px',
    borderRadius: 7,
    fontSize: 15,
    fontWeight: 450,
    color: isActive ? '#1a6bc4' : 'rgba(0,0,0,0.72)',
    background: isActive ? '#EBF4FF' : 'transparent',
    cursor: 'pointer',
    transition: 'background 0.12s ease, color 0.12s ease',
    whiteSpace: 'nowrap',
    width: '100%',
    border: 'none',
    textAlign: 'left',
  };

  const content = (
    <Fragment>
      {icon}
      {(!icon || !onlyShowIcon) && label}
      {hasChildren && <ChevRight />}
    </Fragment>
  );

  if (hasChildren) {
    return (
      <div
        ref={wrapRef}
        style={{position: 'relative'}}
        onMouseEnter={() => { on(); setHovered(true); }}
        onMouseLeave={() => { off(); setHovered(false); }}
      >
        <div style={rowStyle} data-menu-item-id={item.id}>
          {content}
        </div>
        <DropPanel
          items={item.children!}
          open={open}
          depth={depth + 1}
          unstyled={unstyled}
          onlyShowIcon={onlyShowIcon}
          matchDescendants={matchDescendants}
          iconClassName={iconClassName}
          iconSize={iconSize}
          className={className}
        />
      </div>
    );
  }

  if (item.type === 'link') {
    return (
      <a
        href={item.action}
        target={item.target}
        style={rowStyle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-menu-item-id={item.id}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      end={typeof matchDescendants === 'function'
        ? matchDescendants(item.action) : matchDescendants}
      style={({isActive: navActive}) => ({
        ...rowStyle,
        color: navActive || isActive ? '#1a6bc4' : rowStyle.color,
        background: navActive || isActive ? '#EBF4FF' : rowStyle.background,
      })}
      to={item.action}
      target={item.target}
      data-menu-item-id={item.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {content}
    </NavLink>
  );
}

interface MenuItemProps extends React.RefAttributes<HTMLAnchorElement> {
  item: MenuItemConfig;
  iconClassName?: string;
  className?: (props: {isActive: boolean}) => string | undefined;
  matchDescendants?: MatchDescendants;
  onlyShowIcon?: boolean;
  iconSize?: IconSize;
  unstyled?: boolean;
}

export const CustomMenuItem = forwardRef<HTMLAnchorElement, MenuItemProps>(
  (
    {item, className, matchDescendants, unstyled, onlyShowIcon,
      iconClassName, iconSize = 'sm', ...linkProps},
    ref,
  ) => {
    const hasChildren = !!(item.children?.length);
    const {open, on, off} = useHoverDelay(0, 140);
    const wrapRef = useRef<HTMLDivElement>(null);
    const navbarRef = useRef<HTMLElement | null>(null);

    // Find navbar element once
    useEffect(() => {
      if (wrapRef.current) {
        navbarRef.current =
          wrapRef.current.closest('[data-navbar]') as HTMLElement
          ?? wrapRef.current.closest('nav') as HTMLElement
          ?? wrapRef.current.closest('header') as HTMLElement;
      }
    }, []);

    const IconCmp = item.icon && createSvgIconFromTree(item.icon);
    let icon = IconCmp ? <IconCmp size={iconSize} className={iconClassName} /> : null;
    const label = <Trans message={item.label} />;

    if (icon && onlyShowIcon && label) {
      icon = <Tooltip label={label} placement="right">{icon}</Tooltip>;
    }

    const baseClassName =
      !unstyled && 'whitespace-nowrap flex items-center justify-start gap-10';
    const focusClassNames =
      !unstyled && 'outline-none focus-visible:ring-2';

    const content = (
      <Fragment>
        {icon}
        {(!icon || !onlyShowIcon) && label}
        {hasChildren && <ChevDown open={open} />}
      </Fragment>
    );

    if (hasChildren) {
      return (
        <div
          ref={wrapRef}
          style={{position: 'relative'}}
          onMouseEnter={on}
          onMouseLeave={off}
        >
          <div
            className={clsx(
              baseClassName,
              'cursor-pointer',
              className?.({isActive: false}),
              focusClassNames,
            )}
            data-menu-item-id={item.id}
          >
            {content}
          </div>

          {/* ✅ Multi-column dropdown anchored to navbar bottom */}
          <DropPanel
            items={item.children!}
            open={open}
            depth={0}
            anchorEl={wrapRef.current}
            navbarEl={navbarRef.current}
            unstyled={unstyled}
            onlyShowIcon={onlyShowIcon}
            matchDescendants={matchDescendants}
            iconClassName={iconClassName}
            iconSize={iconSize}
            className={className}
          />
        </div>
      );
    }

    if (item.type === 'link') {
      return (
        <a
          className={clsx(baseClassName, className?.({isActive: false}), focusClassNames)}
          href={item.action}
          target={item.target}
          data-menu-item-id={item.id}
          ref={ref}
          {...linkProps}
        >
          {content}
        </a>
      );
    }

    return (
      <NavLink
        end={typeof matchDescendants === 'function'
          ? matchDescendants(item.action) : matchDescendants}
        className={props =>
          clsx(baseClassName, className?.(props), focusClassNames)}
        to={item.action}
        target={item.target}
        data-menu-item-id={item.id}
        ref={ref}
        {...linkProps}
      >
        {content}
      </NavLink>
    );
  },
);